/**
 * Deadline alert center — bottom-anchored attention card.
 *
 * Mounted once at Layout level. Polls /api/tasks/deadlines/upcoming
 * every 60s and ticks the countdown every 15s in between.
 *
 * Shows ONE primary alert at a time (the most urgent), with a small
 * "+N more" pill to expand the rest. Center-bottom positioning + the
 * sw-glow-{urgent,warning} ripple on the card itself make it impossible
 * to miss without being a blocking modal.
 *
 * Quick actions inline so the user can dispatch the alert in one tap:
 *   - Done           PATCH /tasks/:id { status: "completed" }
 *   - +15 min / +1 h PATCH /tasks/:id { deadline: <new> }   ← prevents overdue
 *   - Pick…          goes to /app/tasks/:id/reschedule (picker on its own page)
 *   - Open task      navigates to /app/tasks?focus=:id
 *
 * Snooze + dismiss are stored client-side keyed by `<id>|<deadline_iso>`
 * so rescheduling automatically re-fires when the new deadline approaches.
 *
 * Browser Notification API fires alongside the in-app card once the user
 * grants permission via the inline prompt (never auto-asked).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AlertTriangle, Clock, X, Bell, BellOff, MoonStar,
  ChevronRight, Check, CalendarClock, ChevronUp, ChevronDown,
} from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const POLL_INTERVAL_MS  = 60_000;
const TICK_INTERVAL_MS  = 15_000;
const STORAGE_KEY       = "sw_deadline_alerts_v1";
const PERM_PROMPTED_KEY = "sw_notif_perm_prompted_v1";

// ── Public component ────────────────────────────────────────────────────────

export default function DeadlineAlertCenter() {
  const nav = useNavigate();
  const location = useLocation();
  const [tasks, setTasks] = useState([]);
  const [serverSkewMs, setServerSkewMs] = useState(0);
  const [tickNow, setTickNow] = useState(Date.now());
  const [state, setState] = usePersistentState(STORAGE_KEY, {});
  const [expanded, setExpanded] = useState(false);
  const [notifPermission, setNotifPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );
  const [showPermPrompt, setShowPermPrompt] = useState(false);
  const lastFiredRef = useRef(new Set());

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/tasks/deadlines/upcoming", {
        params: { window_minutes: 15, overdue_hours: 24 },
      });
      setTasks(r.data.tasks || []);
      if (r.data.server_time) {
        setServerSkewMs(new Date(r.data.server_time).getTime() - Date.now());
      }
    } catch { /* best-effort; ignore */ }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [refresh]);

  useEffect(() => {
    const t = setInterval(() => setTickNow(Date.now()), TICK_INTERVAL_MS);
    return () => clearInterval(t);
  }, []);

  const now = tickNow + serverSkewMs;
  const visible = useMemo(() => {
    return tasks
      .map((t) => decorate(t, now, state))
      .filter((t) => t && !t.hidden)
      .sort((a, b) => urgencyRank(a.urgency) - urgencyRank(b.urgency) || a.deadlineMs - b.deadlineMs);
  }, [tasks, now, state]);

  // Browser notifications — one per (alertKey, urgency) so escalation
  // (soon → very_soon → overdue) re-fires once per stage.
  useEffect(() => {
    if (notifPermission !== "granted") return;
    for (const a of visible) {
      const fireKey = `${a.alertKey}|${a.urgency}`;
      if (lastFiredRef.current.has(fireKey)) continue;
      lastFiredRef.current.add(fireKey);
      try {
        const n = new Notification(
          a.urgency === "overdue" ? "Deadline missed" : "Deadline approaching",
          { body: `${a.title} — ${a.countdownText}`, tag: a.alertKey, icon: "/favicon.ico" }
        );
        n.onclick = () => { window.focus(); nav(`/app/tasks?focus=${a.id}`); n.close(); };
      } catch { /* some browsers throw in background */ }
    }
  }, [visible, notifPermission, nav]);

  useEffect(() => {
    if (visible.length === 0) return;
    if (notifPermission !== "default") return;
    if (localStorage.getItem(PERM_PROMPTED_KEY) === "1") return;
    setShowPermPrompt(true);
  }, [visible.length, notifPermission]);

  // Tab-title flash: alternate the browser tab title while the tab is
  // hidden AND there's something urgent. The visibilitychange listener
  // restores the title (and stops the flashing) the moment the user
  // returns to the tab.
  useEffect(() => {
    const counts = visible.reduce((acc, a) => {
      if (a.urgency === "overdue") acc.overdue += 1;
      else if (a.urgency === "very_soon") acc.verySoon += 1;
      else acc.soon += 1;
      return acc;
    }, { overdue: 0, verySoon: 0, soon: 0 });

    const total = counts.overdue + counts.verySoon + counts.soon;
    const flashLabel = counts.overdue > 0
      ? `⚠ ${counts.overdue} overdue${counts.verySoon ? ` · ${counts.verySoon} due now` : ""}`
      : counts.verySoon > 0
      ? `⏰ ${counts.verySoon} deadline${counts.verySoon === 1 ? "" : "s"} due now`
      : counts.soon > 0
      ? `⏰ ${counts.soon} deadline${counts.soon === 1 ? "" : "s"} approaching`
      : null;

    if (!flashLabel) return;  // nothing to advertise

    return startTitleFlash(flashLabel);
  }, [visible]);

  // Optimistic task patching — when the user takes action, drop the task
  // from local state immediately so the alert disappears without waiting
  // for the next poll. Backend mutation runs in parallel.
  const patchTask = async (taskId, payload, successMsg) => {
    setTasks((cur) => cur.filter((t) => t.id !== taskId));
    try {
      await api.patch(`/tasks/${taskId}`, payload);
      if (successMsg) toast.success(successMsg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
      refresh();  // refetch so the alert reappears if the mutation failed
    }
  };

  const grantNotif = async () => {
    localStorage.setItem(PERM_PROMPTED_KEY, "1");
    setShowPermPrompt(false);
    try {
      const r = await Notification.requestPermission();
      setNotifPermission(r);
    } catch { /* nothing to do */ }
  };
  const dismissPerm = () => {
    localStorage.setItem(PERM_PROMPTED_KEY, "1");
    setShowPermPrompt(false);
  };

  // The reschedule page is the answer to this alert — don't cover it.
  if (/^\/app\/tasks\/[^/]+\/reschedule/.test(location.pathname)) return null;
  if (visible.length === 0 && !showPermPrompt) return null;

  const primary = visible[0];
  const rest    = visible.slice(1);

  const onSnooze = (a, ms) => setState((p) => ({
    ...p,
    [a.alertKey]: { ...(p[a.alertKey] || {}), snoozedUntil: Date.now() + ms },
  }));
  const onDismiss = (a) => setState((p) => ({
    ...p,
    [a.alertKey]: { ...(p[a.alertKey] || {}), dismissed: true },
  }));

  return (
    <>
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[55] flex flex-col items-center gap-2 w-[min(560px,calc(100vw-2rem))] pointer-events-none">
        {showPermPrompt && (
          <PermissionPrompt onGrant={grantNotif} onDismiss={dismissPerm} />
        )}

        {primary && (
          <>
            <PrimaryAlert
              alert={primary}
              onOpen={() => nav(`/app/tasks?focus=${primary.id}`)}
              onMarkDone={() => patchTask(primary.id, { status: "completed" }, "Marked complete")}
              onExtend={(minutes) => {
                const base = new Date(primary.deadline).getTime();
                const target = base < Date.now() ? Date.now() : base;
                const newDeadline = new Date(target + minutes * 60_000).toISOString();
                patchTask(primary.id, { deadline: newDeadline }, `Extended by ${minutes} min`);
              }}
              onReschedule={() => nav(`/app/tasks/${primary.id}/reschedule`, {
                state: { from: location.pathname + location.search },
              })}
              onSnooze={(ms) => onSnooze(primary, ms)}
              onDismiss={() => onDismiss(primary)}
            />

            {rest.length > 0 && (
              <button
                onClick={() => setExpanded((v) => !v)}
                className="pointer-events-auto h-7 px-3 inline-flex items-center gap-1.5 bg-white/95 backdrop-blur border border-slate-200 rounded-full text-[12px] font-medium text-slate-700 hover:border-slate-400 shadow-md transition"
              >
                {expanded ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
                {rest.length} more deadline{rest.length === 1 ? "" : "s"} pending
              </button>
            )}

            {expanded && rest.length > 0 && (
              <div className="pointer-events-auto w-full bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden sw-rise-bounce max-h-[40vh] overflow-y-auto">
                <ul>
                  {rest.map((a) => (
                    <SecondaryRow
                      key={a.alertKey}
                      alert={a}
                      onOpen={() => nav(`/app/tasks?focus=${a.id}`)}
                      onSnooze={(ms) => onSnooze(a, ms)}
                      onDismiss={() => onDismiss(a)}
                    />
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

// ── The big "action required" card ─────────────────────────────────────────

function PrimaryAlert({ alert, onOpen, onMarkDone, onExtend, onReschedule, onSnooze, onDismiss }) {
  const cardRef = useRef(null);

  // One-shot attention shake on appearance, then remove the class so the
  // animation doesn't replay on every re-render (countdown ticks).
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    el.classList.add("sw-shake-attn");
    const t = setTimeout(() => el.classList.remove("sw-shake-attn"), 1100);
    return () => clearTimeout(t);
  }, [alert.alertKey]);

  const isOverdue = alert.urgency === "overdue";
  const isVerySoon = alert.urgency === "very_soon";
  const glowClass = isOverdue ? "sw-glow-urgent" : (isVerySoon ? "sw-glow-warning" : "");
  const ringClass = isOverdue
    ? "border-red-300"
    : isVerySoon
    ? "border-amber-300"
    : "border-blue-300";
  const iconCls = isOverdue
    ? "bg-red-100   text-red-700   sw-pulse-urgent"
    : isVerySoon
    ? "bg-amber-100 text-amber-700 sw-pulse-warning"
    : "bg-blue-100  text-blue-700";

  return (
    <div
      ref={cardRef}
      className={
        "pointer-events-auto w-full rounded-2xl bg-white border-2 " +
        ringClass + " " + glowClass + " sw-rise-bounce"
      }
      role="alert"
      aria-live="assertive"
      data-testid={`deadline-alert-${alert.id}`}
    >
      {/* Header */}
      <div className="p-4 pb-3 flex items-start gap-3">
        <div className={"w-11 h-11 rounded-full grid place-items-center shrink-0 " + iconCls}>
          {isOverdue ? <AlertTriangle size={18} /> : <Clock size={18} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={
              "text-[10.5px] font-bold uppercase tracking-[0.18em] " +
              (isOverdue ? "text-red-700" : isVerySoon ? "text-amber-800" : "text-blue-700")
            }>
              {isOverdue ? "Overdue · Action required" : "Deadline approaching"}
            </span>
            <span className="ml-auto text-[11px] font-bold font-mono text-slate-700 shrink-0">
              {alert.countdownText}
            </span>
            <button
              onClick={onDismiss}
              aria-label="Dismiss"
              className="-mt-1 -mr-1 h-6 w-6 grid place-items-center text-slate-400 hover:text-slate-900 rounded hover:bg-slate-100 shrink-0"
            >
              <X size={14} />
            </button>
          </div>
          <h3 className="text-[15px] font-bold text-slate-900 mt-1.5 leading-snug line-clamp-2">
            {alert.title}
          </h3>
          {(alert.chat_name || alert.assignee) && (
            <div className="text-[12px] text-slate-500 mt-1 truncate">
              {alert.chat_name && <span>{alert.chat_name}</span>}
              {alert.chat_name && alert.assignee && <span className="mx-1 text-slate-300">·</span>}
              {alert.assignee && <span>@{alert.assignee}</span>}
            </div>
          )}
        </div>
      </div>

      {/* Primary actions — these resolve the alert. Buttons sized for thumbs. */}
      <div className="px-4 pb-3 grid grid-cols-2 gap-2">
        <button
          onClick={onMarkDone}
          className="h-10 inline-flex items-center justify-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-[13px] font-semibold transition active:scale-[0.98]"
          data-testid="alert-mark-done"
        >
          <Check size={14} /> Mark done
        </button>
        <button
          onClick={onOpen}
          className="h-10 inline-flex items-center justify-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-[13px] font-semibold transition active:scale-[0.98]"
        >
          Open task <ChevronRight size={14} />
        </button>
      </div>

      {/* Extend / reschedule — keeps the task alive without dropping it */}
      <div className="px-4 pb-4 flex items-center gap-1.5 border-t border-slate-100 pt-3">
        <span className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-slate-400 mr-1">
          Extend
        </span>
        <button
          onClick={() => onExtend(15)}
          className="h-8 px-2.5 border border-slate-200 hover:border-slate-400 rounded-md text-[12px] font-medium text-slate-700 transition"
          data-testid="alert-extend-15"
        >
          +15 min
        </button>
        <button
          onClick={() => onExtend(60)}
          className="h-8 px-2.5 border border-slate-200 hover:border-slate-400 rounded-md text-[12px] font-medium text-slate-700 transition"
        >
          +1 hour
        </button>
        <button
          onClick={onReschedule}
          className="h-8 px-2.5 inline-flex items-center gap-1 border border-slate-200 hover:border-slate-400 rounded-md text-[12px] font-medium text-slate-700 transition"
        >
          <CalendarClock size={12} /> Pick…
        </button>
        <SnoozeMenu onSnooze={onSnooze} />
      </div>
    </div>
  );
}

// ── Compact rows for the expandable "+N more" stack ─────────────────────────

function SecondaryRow({ alert, onOpen, onSnooze, onDismiss }) {
  const isOverdue = alert.urgency === "overdue";
  return (
    <li className="px-3 py-2.5 flex items-center gap-3 border-b last:border-b-0 border-slate-100 hover:bg-slate-50">
      <div className={
        "w-7 h-7 rounded-full grid place-items-center shrink-0 " +
        (isOverdue ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700")
      }>
        {isOverdue ? <AlertTriangle size={12} /> : <Clock size={12} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold text-slate-900 truncate">{alert.title}</div>
        <div className="text-[11px] text-slate-500 font-mono">{alert.countdownText}</div>
      </div>
      <button
        onClick={onOpen}
        className="h-7 px-2 text-[11.5px] font-medium text-slate-700 border border-slate-200 hover:border-slate-400 rounded"
      >
        Open
      </button>
      <SnoozeMenu onSnooze={onSnooze} compact />
      <button
        onClick={onDismiss}
        aria-label="Dismiss"
        className="h-7 w-7 grid place-items-center text-slate-400 hover:text-slate-900 rounded"
      >
        <X size={13} />
      </button>
    </li>
  );
}

// ── Snooze dropdown ────────────────────────────────────────────────────────

function SnoozeMenu({ onSnooze, compact = false }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);
  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        title="Snooze"
        className={
          (compact
            ? "h-7 w-7 grid place-items-center text-slate-500"
            : "h-8 px-2.5 inline-flex items-center gap-1 text-[12px] font-medium text-slate-700") +
          " border border-slate-200 hover:border-slate-400 rounded-md transition"
        }
      >
        <MoonStar size={compact ? 12 : 12} /> {!compact && "Snooze"}
      </button>
      {open && (
        <div className="absolute right-0 bottom-full mb-1.5 bg-white border border-slate-200 rounded-lg shadow-lg py-1 min-w-[140px] z-10 sw-pop">
          {[
            ["5 minutes",  5  * 60_000],
            ["15 minutes", 15 * 60_000],
            ["1 hour",     60 * 60_000],
          ].map(([label, ms]) => (
            <button
              key={label}
              onClick={() => { onSnooze(ms); setOpen(false); }}
              className="w-full text-left px-3 py-1.5 text-[12.5px] text-slate-700 hover:bg-slate-50"
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Permission prompt — only shown once per device ─────────────────────────

function PermissionPrompt({ onGrant, onDismiss }) {
  if (typeof Notification === "undefined") return null;
  return (
    <div className="pointer-events-auto sw-rise-bounce w-full rounded-xl shadow-lg border border-slate-200 bg-white p-3 flex items-start gap-3">
      <div className="w-9 h-9 rounded-full bg-blue-50 grid place-items-center shrink-0">
        <Bell size={15} className="text-blue-600" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold text-slate-900">Enable browser notifications</div>
        <div className="text-[11.5px] text-slate-500 mt-0.5">
          Get alerted about deadlines even when this tab isn't focused.
        </div>
        <div className="mt-2 flex gap-1.5">
          <button onClick={onGrant} className="h-7 px-3 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-[12px] font-semibold transition">Enable</button>
          <button onClick={onDismiss} className="h-7 px-3 border border-slate-200 hover:border-slate-400 rounded-md text-[12px] text-slate-700 inline-flex items-center gap-1">
            <BellOff size={11} /> Not now
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────

function urgencyRank(u) {
  return u === "overdue" ? 0 : u === "very_soon" ? 1 : 2;
}

function decorate(task, now, state) {
  if (!task.deadline) return null;
  const deadlineMs = new Date(task.deadline).getTime();
  if (!Number.isFinite(deadlineMs)) return null;

  const diffMs = deadlineMs - now;
  const minutesUntil = Math.round(diffMs / 60_000);

  let urgency;
  if (diffMs < 0) urgency = "overdue";
  else if (minutesUntil <= 5)  urgency = "very_soon";
  else if (minutesUntil <= 15) urgency = "soon";
  else return null;

  const alertKey = `${task.id}|${task.deadline}`;
  const entry = state[alertKey] || {};
  const snoozedUntil = entry.snoozedUntil || 0;
  const hidden = entry.dismissed || snoozedUntil > Date.now();

  return {
    ...task,
    deadlineMs,
    urgency,
    alertKey,
    hidden,
    countdownText: formatCountdown(diffMs),
  };
}

function formatCountdown(diffMs) {
  const abs = Math.abs(diffMs);
  const mins = Math.round(abs / 60_000);
  if (mins < 1)  return diffMs < 0 ? "Just missed"   : "<1 min left";
  if (mins < 60) return diffMs < 0 ? `${mins}m late` : `${mins}m left`;
  const hours = Math.round(mins / 60);
  return diffMs < 0 ? `${hours}h late` : `${hours}h left`;
}

function usePersistentState(key, fallback) {
  const [val, setVal] = useState(() => {
    try { return JSON.parse(localStorage.getItem(key) || "null") ?? fallback; }
    catch { return fallback; }
  });
  useEffect(() => {
    try { localStorage.setItem(key, JSON.stringify(val)); } catch { /* quota */ }
  }, [key, val]);
  return [val, setVal];
}

// ── Tab-title flash ────────────────────────────────────────────────────────

const TITLE_FLASH_INTERVAL_MS = 1200;

/**
 * While the tab is hidden, alternate document.title between its original
 * value and the supplied alert label. Restores immediately when the tab
 * becomes visible, or when the returned cleanup runs.
 *
 * Captures the title that was set BEFORE we started flashing (not at
 * each interval tick) so other code that updates the title doesn't fight
 * with us — we restore to a known good baseline on cleanup.
 */
function startTitleFlash(label) {
  if (typeof document === "undefined") return () => {};

  const original = document.title;
  let intervalId = null;
  let toggled = false;

  const start = () => {
    if (intervalId) return;
    toggled = false;
    intervalId = setInterval(() => {
      toggled = !toggled;
      document.title = toggled ? label : original;
    }, TITLE_FLASH_INTERVAL_MS);
  };
  const stop = () => {
    if (intervalId) { clearInterval(intervalId); intervalId = null; }
    document.title = original;
  };

  const onVisibility = () => {
    if (document.visibilityState === "hidden") start();
    else stop();
  };

  // Only flash when the tab is hidden — pointless attention-grab when
  // the user is already looking at the tab.
  if (document.visibilityState === "hidden") start();
  document.addEventListener("visibilitychange", onVisibility);

  return () => {
    document.removeEventListener("visibilitychange", onVisibility);
    stop();
  };
}
